import React from "react";
import clsx from "clsx"; // 👈 optional: helps manage classNames better

interface SleekButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "light";
  children: React.ReactNode;
}

const SleekButton: React.FC<SleekButtonProps> = ({
  children,
  variant = "primary",
  className,
  ...props
}) => {
  const baseClasses = `
    group relative overflow-hidden px-12 py-4 rounded-full text-base font-semibold cursor-pointer
    transition-all motion-reduce:transition-none duration-300 min-w-36 text-center focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1
    hover:scale-105 transition-transform duration-200
  `;

  const variants: Record<typeof variant, string> = {
 primary: `
  inline-flex items-center justify-center
  rounded-full w-auto
  bg-gradient-to-t from-[#416BE3] to-[#243B7D]
  text-white text-sm font-medium

  px-5 py-3

  border border-white/10
  ring-0 ring-offset-0
  dark:ring-1 dark:ring-white/20 dark:ring-offset-2 dark:ring-offset-[#000619]

  shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_8px_16px_rgba(0,0,0,0.35)]

  hover:scale-105 transition-transform duration-200
`,
    light: clsx(
      "inline-flex items-center justify-center px-5 py-3 text-sm font-medium",
      "text-slate-800 dark:text-white",
      "focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-1",
      "border border-slate-200/70 hover:border-slate-300/80",
      "dark:border-white/20 dark:hover:border-white/35",
      "bg-white dark:bg-white/[0.08]",
      "shadow-[inset_0_1px_0_rgba(255,255,255,1),0_1px_2px_rgba(15,23,42,0.05),0_4px_16px_rgba(65,107,227,0.12)]",
      "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]",
      "hover:shadow-[inset_0_1px_0_rgba(255,255,255,1),0_2px_6px_rgba(15,23,42,0.06),0_10px_28px_rgba(65,107,227,0.16)]",
      "dark:hover:text-gray-900 dark:hover:border-gray-200",
      "dark:hover:shadow-[inset_0_1px_0_rgba(255,255,255,1),0_2px_8px_rgba(0,0,0,0.08)]",
      "active:scale-95"
    ),
  };

  const sweepByVariant: Record<typeof variant, string> = {
    primary:
      "bg-gradient-to-l from-[#5C81EA] via-[#416BE3] to-[#243B7D]",
    light:
      "bg-gradient-to-l from-[#416BE3] via-[#2F58CC] to-[#1D3F9F] dark:from-white dark:via-white dark:to-slate-100",
  };

  return (
    <button
      className={clsx(baseClasses, variants[variant], className)}
      {...props}
    >
      <span
        aria-hidden
        className={clsx(
          "absolute -inset-y-1 -left-1/3 w-[170%] rounded-full -translate-x-full transition-transform duration-500 ease-out group-hover:translate-x-0",
          sweepByVariant[variant]
        )}
      />
      <span
        className={clsx(
          "relative z-10 transition-colors duration-300",
          variant === "light" ? "group-hover:text-white dark:group-hover:text-gray-900" : "group-hover:text-white"
        )}
      >
        {children}
      </span>
    </button>
  );
};

export default SleekButton;
